import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { AuditLogType } from '@/api/model'
import type { OrderType } from '@/api/model'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'

// ─── Richer local types (API returns these fields, orval types are incomplete) ─

interface AuditLogBase {
  timestamp: string
  companyId: number
  userId: string | null
  userName: string | null
  type?: string
}

interface AffectedUserAuditLog extends AuditLogBase {
  affectedUserId: string | null
  affectedUserName: string | null
  affectedUserIsDeleted: boolean
}

interface PermissionAuditLog extends AffectedUserAuditLog {
  entityType: string | null
  permissionName: string | null
}

interface ActionModuleAuditLog extends AuditLogBase {
  actionModuleId: number
  actionModuleName: string | null
  actionModuleInstanceId: number
}

export type RichAuditLogItem = AuditLogBase | AffectedUserAuditLog | PermissionAuditLog | ActionModuleAuditLog

// ─── Type guards ──────────────────────────────────────────────────────────────

function hasAffectedUser(item: AuditLogBase): item is AffectedUserAuditLog {
  return item.type === AuditLogType.UserCreated || item.type === AuditLogType.UserDeleted
    || item.type === AuditLogType.PermissionAdded || item.type === AuditLogType.PermissionRevoked
}

function hasPermission(item: AuditLogBase): item is PermissionAuditLog {
  return item.type === AuditLogType.PermissionAdded || item.type === AuditLogType.PermissionRevoked
}

function hasActionModule(item: AuditLogBase): item is ActionModuleAuditLog {
  return item.type === AuditLogType.ActionModuleInstanceCompletey
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const AuditLogSortFields = {
  Timestamp: 'timestamp',
  UserId: 'userId',
} as const

function getTypeLabels(t: TFunction): Record<string, string> {
  return {
    [AuditLogType.UserCreated]: t('auditLogType.UserCreated'),
    [AuditLogType.UserDeleted]: t('auditLogType.UserDeleted'),
    [AuditLogType.PermissionAdded]: t('auditLogType.PermissionAdded'),
    [AuditLogType.PermissionRevoked]: t('auditLogType.PermissionRevoked'),
    [AuditLogType.ActionModuleInstanceCompletey]: t('auditLogType.ActionModuleInstanceCompletey'),
  }
}

// entityType/permissionName are raw strings from the backend (not a generated enum) — fall back
// to the raw value if the backend returns a new value that isn't translated here yet.
function translateRaw(t: TFunction, ns: string, raw: string): string {
  const key = `${ns}.${raw}`
  const translated = t(key)
  return translated === key ? raw : translated
}

const TYPE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  [AuditLogType.UserCreated]: 'default',
  [AuditLogType.UserDeleted]: 'destructive',
  [AuditLogType.PermissionAdded]: 'secondary',
  [AuditLogType.PermissionRevoked]: 'outline',
  [AuditLogType.ActionModuleInstanceCompletey]: 'secondary',
}

function formatTimestamp(value: string) {
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function Cell({ value }: { value?: string | null }) {
  return value
    ? <span>{value}</span>
    : <span className="text-muted-foreground">—</span>
}

// ─── Component ────────────────────────────────────────────────────────────────

interface AuditLogsDataTableProps {
  isLoading: boolean
  isError: boolean
  items: RichAuditLogItem[]
  currentSort?: string
  currentOrder?: OrderType
  onSort: (sort?: string, order?: OrderType) => void
}

export function AuditLogsDataTable({
  isLoading,
  isError,
  items,
  currentSort,
  currentOrder,
  onSort,
}: AuditLogsDataTableProps) {
  const { t } = useTranslation(['audit-logs', 'common'])
  const typeLabels = getTypeLabels(t)

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <SortableTableHead field={AuditLogSortFields.Timestamp} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('table.columns.dateTime')}
          </SortableTableHead>
          <SortableTableHead field={AuditLogSortFields.UserId} currentSort={currentSort} currentOrder={currentOrder} onSort={onSort}>
            {t('table.columns.executingUser')}
          </SortableTableHead>
          <TableHead>{t('table.columns.action')}</TableHead>
          <TableHead>{t('table.columns.affectedUser')}</TableHead>
          <TableHead>{t('table.columns.userDeleted')}</TableHead>
          <TableHead>{t('table.columns.entity')}</TableHead>
          <TableHead>{t('table.columns.permission')}</TableHead>
          <TableHead>{t('table.columns.actionModule')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable isLoading={isLoading} isError={isError} isEmpty={items.length === 0} colSpan={8}>
          {items.map((item, i) => (
          <TableRow key={`${item.timestamp}-${i}`}>
            <TableCell className="text-muted-foreground tabular-nums whitespace-nowrap">
              {formatTimestamp(item.timestamp)}
            </TableCell>
            <TableCell className="font-medium">
              <Cell value={item.userName} />
            </TableCell>
            <TableCell>
              <Badge variant={TYPE_VARIANTS[item.type ?? ''] ?? 'outline'}>
                {typeLabels[item.type ?? ''] ?? item.type ?? '—'}
              </Badge>
            </TableCell>
            <TableCell>
              <Cell value={hasAffectedUser(item) ? item.affectedUserName : null} />
            </TableCell>
            <TableCell>
              {hasAffectedUser(item)
                ? <Badge variant={item.affectedUserIsDeleted ? 'destructive' : 'outline'}>
                    {item.affectedUserIsDeleted ? t('table.yes') : t('table.no')}
                  </Badge>
                : <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell>
              <Cell value={hasPermission(item) && item.entityType ? translateRaw(t, 'entityType', item.entityType) : null} />
            </TableCell>
            <TableCell>
              <Cell value={hasPermission(item) && item.permissionName ? translateRaw(t, 'permissionName', item.permissionName) : null} />
            </TableCell>
            <TableCell>
              <Cell value={hasActionModule(item) ? item.actionModuleName : null} />
            </TableCell>
          </TableRow>
          ))}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
